import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Icon from "../../../components/AppIcon"
import Button from "../../../components/ui/Button"
import Input from "../../../components/ui/Input"
import {
  subscribeLiveClasses,
  createLiveClass,
  updateLiveClass,
  deleteLiveClass,
} from "../../../utils/liveClassHelpers"
import { authHelpers } from "../../../firebaseClient"

const emptyForm = {
  title: "",
  description: "",
  scheduledDate: "",
  scheduledTime: "",
  durationMinutes: 60,
  capacity: "",
}

const STATUS_STYLES = {
  scheduled: "bg-muted text-muted-foreground",
  live: "bg-destructive/10 text-destructive",
  ended: "bg-success/10 text-success",
}

const LiveClassesTab = ({ orgId, courseId }) => {
  const navigate = useNavigate()
  const [liveClasses, setLiveClasses] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [createError, setCreateError] = useState("")
  const [currentUser, setCurrentUser] = useState(null)

  useEffect(() => {
    authHelpers.getSession().then(({ session }) => setCurrentUser(session?.user || null))
  }, [])

  useEffect(() => {
    if (!orgId) return
    const unsubscribe = subscribeLiveClasses(orgId, (all) => {
      setLiveClasses(all?.filter((c) => c?.courseId === courseId))
    })
    return unsubscribe
  }, [orgId, courseId])

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e?.target?.value }))
  }

    const handleCreate = async () => {
    if (!form?.title?.trim()) return
    setSaving(true)
    setCreateError("")

    const scheduledAtMs =
      form?.scheduledDate && form?.scheduledTime
        ? new Date(`${form.scheduledDate}T${form.scheduledTime}`).getTime()
        : null

    try {
      await createLiveClass(orgId, {
        title: form?.title,
        description: form?.description,
        courseId,
        instructorUid: currentUser?.uid,
        instructorName: currentUser?.displayName || currentUser?.email || "Instructor",
        scheduledAtMs,
        durationMinutes: Number(form?.durationMinutes) || 60,
        capacity: form?.capacity ? Number(form?.capacity) : null,
      })
      setShowForm(false)
      setForm(emptyForm)
    } catch (e) {
      console.error("[LiveClassesTab] createLiveClass failed:", e)
      setCreateError("Couldn't schedule the class. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (classId) => {
    if (!window.confirm("Delete this live class?")) return
    await deleteLiveClass(orgId, classId)
  }

  const formatSchedule = (ms) => {
    if (!ms) return "Not scheduled"
    return new Date(ms).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">Live Classes</h2>
        <Button variant="outline" iconName="Plus" iconPosition="left" onClick={() => setShowForm((s) => !s)}>
          {showForm ? "Cancel" : "Schedule Live Class"}
        </Button>
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <Input label="Title" placeholder="e.g. Smart Contract Security — Week 3" value={form?.title} onChange={handleFieldChange("title")} />

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Description</label>
            <textarea
              rows={2}
              value={form?.description}
              onChange={handleFieldChange("description")}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Input type="date" label="Date" value={form?.scheduledDate} onChange={handleFieldChange("scheduledDate")} />
            <Input type="time" label="Time" value={form?.scheduledTime} onChange={handleFieldChange("scheduledTime")} />
            <Input type="number" label="Duration (min)" value={form?.durationMinutes} onChange={handleFieldChange("durationMinutes")} />
            <Input type="number" label="Capacity (optional)" placeholder="Unlimited" value={form?.capacity} onChange={handleFieldChange("capacity")} />
          </div>

            <div className="flex items-center justify-end gap-3">
            {createError && <span className="text-sm text-destructive">{createError}</span>}
            <Button variant="default" loading={saving} onClick={handleCreate}>
              Schedule Class
            </Button>
          </div>
        </div>
      )}

      {liveClasses?.length === 0 && !showForm && (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Icon name="Video" size={28} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No live classes scheduled yet for this course.</p>
        </div>
      )}

      <div className="space-y-3">
        {liveClasses?.map((lc) => (
          <div key={lc?.id} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-foreground">{lc?.title}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES?.[lc?.status] || STATUS_STYLES.scheduled}`}>
                  {lc?.status}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{formatSchedule(lc?.scheduledAtMs)}</p>
              <p className="text-xs text-muted-foreground">
                {lc?.durationMinutes} min · {lc?.attendeeCount || 0} attendee{lc?.attendeeCount === 1 ? "" : "s"}
              </p>
            </div>
                        <div className="flex items-center gap-2">
              {lc?.status !== "ended" && (
                <Button
                  variant="default"
                  size="sm"
                  iconName="Video"
                  iconPosition="left"
                  onClick={() => navigate(`/live-classroom-host?orgId=${orgId}&classId=${lc?.id}`)}
                >
                  {lc?.status === "live" ? "Rejoin" : "Start Class"}
                </Button>
              )}
              <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => handleDelete(lc?.id)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default LiveClassesTab